import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  BookOpen,
  Plus,
  Search,
  Edit3,
  Trash2,
  RefreshCw,
  FileText,
  Tag,
  FolderOpen,
  AlertCircle,
  CheckCircle,
  X
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'general', label: 'General', color: 'bg-slate-100 text-slate-700' },
  { value: 'transcripts', label: 'Transcripts', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'sops', label: 'SOPs', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'dispute_frameworks', label: 'Dispute Frameworks', color: 'bg-amber-100 text-amber-700' },
  { value: 'letter_examples', label: 'Letter Examples', color: 'bg-amber-100 text-amber-700' },
  { value: 'funding_frameworks', label: 'Funding Frameworks', color: 'bg-amber-100 text-amber-700' },
  { value: 'policy_updates', label: 'Policy Updates', color: 'bg-red-100 text-red-700' },
  { value: 'specialist_notes', label: 'Specialist Notes', color: 'bg-pink-100 text-pink-700' },
];

const getCategoryBadge = (category) => {
  const cat = CATEGORIES.find(c => c.value === category);
  return cat ? (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${cat.color}`}>
      {cat.label}
    </span>
  ) : (
    <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
      {category}
    </span>
  );
};

const KnowledgeBase = () => {
  const { user, getAuthHeaders } = useAuth();
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [newEntry, setNewEntry] = useState({
    title: '',
    category: 'general',
    content: '',
    content_type: 'text',
    tags: '',
    source: 'manual_entry'
  });

  useEffect(() => {
    fetchEntries();
  }, [selectedCategory]);

  const fetchEntries = async () => {
    try {
      // Use new admin-ops endpoint
      const params = {};
      if (selectedCategory && selectedCategory !== 'all') {
        params.category = selectedCategory;
      }
      const query = new URLSearchParams(params).toString();
      const url = `${API}/admin-ops/knowledge-base${query ? `?${query}` : ''}`;
      const response = await axios.get(url, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setEntries(response.data.entries || []);
      // Extract unique categories from entries
      const cats = [...new Set((response.data.entries || []).map(e => e.category))];
      setCategories(cats);
    } catch (error) {
      console.error('Failed to fetch knowledge base:', error);
      toast.error('Failed to load knowledge base');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newEntry.title.trim() || !newEntry.content.trim()) {
      toast.error('Title and content are required');
      return;
    }

    try {
      const entryData = {
        ...newEntry,
        tags: newEntry.tags.split(',').map(t => t.trim()).filter(Boolean)
      };
      // Use new admin-ops endpoint
      await axios.post(`${API}/admin-ops/knowledge-base`, entryData, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Knowledge entry created');
      setShowAddDialog(false);
      setNewEntry({
        title: '',
        category: 'general',
        content: '',
        content_type: 'text',
        tags: '',
        source: 'manual_entry'
      });
      fetchEntries();
    } catch (error) {
      toast.error('Failed to create entry');
    }
  };

  const handleUpdate = async () => {
    if (!editingEntry) return;

    try {
      const entryData = {
        ...editingEntry,
        tags: typeof editingEntry.tags === 'string' 
          ? editingEntry.tags.split(',').map(t => t.trim()).filter(Boolean)
          : editingEntry.tags
      };
      // Use new admin-ops endpoint
      const entryId = editingEntry.entry_id || editingEntry.id;
      await axios.patch(`${API}/admin-ops/knowledge-base/${entryId}`, entryData, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Entry updated');
      setEditingEntry(null);
      fetchEntries();
    } catch (error) {
      toast.error('Failed to update entry');
    }
  };

  const handleDelete = async (entryId) => {
    if (!confirm('Are you sure you want to delete this entry?')) return;

    try {
      // Use new admin-ops endpoint - archive (soft delete)
      await axios.patch(`${API}/admin-ops/knowledge-base/${entryId}`, 
        { status: 'archived' },
        {
          headers: getAuthHeaders(),
          withCredentials: true
        }
      );
      toast.success('Entry archived');
      fetchEntries();
    } catch (error) {
      toast.error('Failed to archive entry');
    }
  };

  const filteredEntries = entries.filter(entry => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      entry.title?.toLowerCase().includes(query) ||
      entry.content?.toLowerCase().includes(query) ||
      entry.tags?.some(t => t.toLowerCase().includes(query))
    );
  });

  if (!['admin', 'specialist', 'platform_owner', 'platform_admin', 'owner'].includes(user?.role)) {
    return (
      <div className="text-center py-12">
        <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
        <h2 className="text-xl font-bold text-stone-900 mb-2">Admin Access Required</h2>
        <p className="text-slate-500">This page is only available to administrators.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="knowledge-base">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-3">
            <BookOpen className="text-amber-500" />
            AI Knowledge Base
          </h1>
          <p className="text-sm text-slate-500">Manage internal AI reference materials</p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} data-testid="add-entry-btn">
          <Plus size={16} className="mr-2" />
          Add Entry
        </Button>
      </div>

      {/* Filters */}
      <div className="elite-card p-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search entries..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORIES.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchEntries}>
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="elite-card p-4 text-center">
          <p className="text-2xl font-bold text-amber-600">{entries.length}</p>
          <p className="text-xs text-slate-500">Total Entries</p>
        </div>
        <div className="elite-card p-4 text-center">
          <p className="text-2xl font-bold text-emerald-600">{entries.filter(e => e.is_active).length}</p>
          <p className="text-xs text-slate-500">Active</p>
        </div>
        <div className="elite-card p-4 text-center">
          <p className="text-2xl font-bold text-slate-600">{categories.length}</p>
          <p className="text-xs text-slate-500">Categories Used</p>
        </div>
        <div className="elite-card p-4 text-center">
          <p className="text-2xl font-bold text-emerald-600">
            {entries.reduce((acc, e) => acc + (e.usage_count || 0), 0)}
          </p>
          <p className="text-xs text-slate-500">Total Uses</p>
        </div>
      </div>

      {/* Entries List */}
      {loading ? (
        <div className="space-y-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-32" />)}</div>
      ) : filteredEntries.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-xl">
          <FolderOpen size={48} className="mx-auto text-slate-400 mb-4" />
          <h3 className="text-lg font-semibold text-stone-900">No Entries Found</h3>
          <p className="text-slate-500">Add your first knowledge base entry to get started</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredEntries.map(entry => (
            <div key={entry.id || entry.entry_id} className="elite-card p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-stone-900">{entry.title}</h3>
                    {getCategoryBadge(entry.category)}
                    {!entry.is_active && entry.status !== 'live' && (
                      <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded">Inactive</span>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 line-clamp-2 mb-2">
                    {entry.content?.substring(0, 200)}...
                  </p>
                  <div className="flex items-center gap-4 text-xs text-slate-500">
                    <span>Source: {entry.source || 'manual'}</span>
                    <span>Created: {new Date(entry.created_at).toLocaleDateString()}</span>
                    {entry.tags?.length > 0 && (
                      <div className="flex items-center gap-1">
                        <Tag size={12} />
                        {entry.tags.slice(0, 3).map((tag, i) => (
                          <span key={i} className="px-1 py-0.5 bg-slate-100 rounded text-[10px]">{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setEditingEntry({
                      ...entry,
                      tags: entry.tags?.join(', ') || ''
                    })}
                  >
                    <Edit3 size={14} />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="text-red-600 hover:bg-red-50"
                    onClick={() => handleDelete(entry.id || entry.entry_id)}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Knowledge Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={newEntry.title}
                  onChange={(e) => setNewEntry({...newEntry, title: e.target.value})}
                  placeholder="Entry title"
                />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={newEntry.category} onValueChange={(v) => setNewEntry({...newEntry, category: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Content</Label>
              <Textarea
                value={newEntry.content}
                onChange={(e) => setNewEntry({...newEntry, content: e.target.value})}
                placeholder="Paste text, transcripts, SOPs, or training materials..."
                rows={10}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Content Type</Label>
                <Select value={newEntry.content_type} onValueChange={(v) => setNewEntry({...newEntry, content_type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Plain Text</SelectItem>
                    <SelectItem value="markdown">Markdown</SelectItem>
                    <SelectItem value="transcript">Transcript</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Tags (comma-separated)</Label>
                <Input
                  value={newEntry.tags}
                  onChange={(e) => setNewEntry({...newEntry, tags: e.target.value})}
                  placeholder="dispute, bureau, strategy"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate}>Create Entry</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editingEntry} onOpenChange={() => setEditingEntry(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Knowledge Entry</DialogTitle>
          </DialogHeader>
          {editingEntry && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input
                    value={editingEntry.title}
                    onChange={(e) => setEditingEntry({...editingEntry, title: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={editingEntry.category} onValueChange={(v) => setEditingEntry({...editingEntry, category: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Content</Label>
                <Textarea
                  value={editingEntry.content}
                  onChange={(e) => setEditingEntry({...editingEntry, content: e.target.value})}
                  rows={10}
                />
              </div>
              <div className="space-y-2">
                <Label>Tags (comma-separated)</Label>
                <Input
                  value={editingEntry.tags}
                  onChange={(e) => setEditingEntry({...editingEntry, tags: e.target.value})}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editingEntry.is_active}
                  onChange={(e) => setEditingEntry({...editingEntry, is_active: e.target.checked})}
                  className="rounded"
                />
                <Label>Active</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingEntry(null)}>Cancel</Button>
            <Button onClick={handleUpdate}>Update Entry</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KnowledgeBase;
